import {AxiosError} from 'axios';
import {useQuery, useQueryClient, UseQueryOptions} from '@tanstack/react-query';

import {api} from '@eksab/api';
import {useAccessToken} from '@eksab/hooks';

import type {Group} from '../types';

const queryKey = (groupId: string | number, isLoggedIn: boolean) => ['groups', `${groupId}`, isLoggedIn];

const queryFn = (groupId: string | number, isLoggedIn: boolean) =>
  api.get<{data: Group}>(`v2/groups/${groupId}${isLoggedIn ? '/auth' : ''}`).then((res) => res.data.data);

export function useGroupQuery(groupId: string | number | undefined, options?: UseQueryOptions<Group, AxiosError>) {
  const isLoggedIn = useAccessToken().isSuccess;

  return useQuery<Group, AxiosError>(queryKey(groupId!, isLoggedIn), () => queryFn(groupId!, isLoggedIn), {
    enabled: !!groupId && isLoggedIn,
    ...options,
  });
}

export function useSetGroupQueryData() {
  const queryClient = useQueryClient();
  const isLoggedIn = !!useAccessToken()?.data;
  const setGroupQueryData = (groupId: string, group: Partial<Group>) => {
    const _queryKey = queryKey(groupId, isLoggedIn);
    const oldGroup = queryClient.getQueryData(_queryKey) as Group;
    queryClient.setQueryData(_queryKey, {...oldGroup, ...group});
  };

  return setGroupQueryData;
}

export function useFetchGroup(groupID: string | number) {
  const queryClient = useQueryClient();
  const isLoggedIn = !!useAccessToken()?.data;
  return () => queryClient.fetchQuery<Me>(queryKey(groupID, isLoggedIn));
}

useGroupQuery.queryFn = queryFn;
useGroupQuery.queryKey = queryKey;
